<template>
  <div class="pl">
    <!-- Top bar -->
    <div class="pl-topbar">
      <div>
        <h1 class="pl-title">Produits</h1>
        <p class="pl-sub">{{ filter.total }} produit(s)</p>
      </div>
      <router-link v-if="auth.can('STOCK')" to="/add-product" class="pl-btn pl-btn-primary">+ Nouveau produit</router-link>
    </div>

    <div class="pl-card">
      <!-- Filters -->
      <div class="pl-toolbar">
        <div class="pl-search">
          <i class="flaticon-search-interface-symbol"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher un produit..."
            aria-label="Rechercher un produit"
            @input="onSearch"
          />
          <button v-if="searchTerm" type="button" class="pl-clear" aria-label="Effacer" @click="clearSearch">×</button>
        </div>

        <div class="pl-filter pl-filter-wide">
          <v-select
            v-model="client_filter"
            class="custom-select-height"
            label="name"
            placeholder="Magasin"
            :options="clientList"
            @search="filterClients"
          />
        </div>
        <div class="pl-filter">
          <v-select v-model="agency_filter" class="custom-select-height" label="name" placeholder="Agence" :options="agenciesList" />
        </div>
        <div class="pl-filter">
          <select v-model="status_filter" class="form-select" aria-label="Statut">
            <option value="">Statut (tous)</option>
            <option value="0">Pas encore reçu</option>
            <option value="1">Reçu</option>
          </select>
        </div>
        <div class="pl-filter pl-date">
          <button type="button" class="pl-date-btn" @click="showCalendar">
            <span>
              {{ selectedDate?.[0]?.format("DD MMM YYYY") || "Date de création" }}
              <template v-if="selectedDate?.[1]"> – {{ selectedDate[1].format("DD MMM YYYY") }}</template>
            </span>
            <i class="flaticon-calendar"></i>
          </button>
          <div v-show="showCalendarBox" class="pl-date-pop">
            <MyDateRangePicker v-model="selectedDate" :enableSecondCalendar="true" @onSelect="showCalendar" />
          </div>
        </div>
        <button v-if="hasFilters" type="button" class="pl-reset" @click="removeFilter">Réinitialiser</button>
      </div>

      <!-- Header (desktop only) -->
      <div class="pl-row pl-head">
        <span>Produit / variante</span>
        <span>Référence</span>
        <span class="num">En stock</span>
        <span class="num">Pas encore reçu</span>
        <span>Statut</span>
        <span></span>
      </div>

      <!-- Loading skeleton -->
      <template v-if="isLoader && !rows.length">
        <div v-for="n in 5" :key="'s' + n" class="pl-row pl-skeleton">
          <div class="pl-product"><div class="pl-thumb sk"></div><div class="sk sk-line"></div></div>
        </div>
      </template>

      <!-- Empty state -->
      <div v-else-if="!rows.length" class="pl-empty">
        <img :src="require('@/assets/images/NoImage.png')" alt="" width="56" />
        <b>Aucun produit trouvé</b>
        <button v-if="hasFilters" type="button" class="pl-btn pl-btn-ghost" @click="removeFilter">Réinitialiser les filtres</button>
      </div>

      <!-- Rows: one product line, then one line per variant -->
      <div v-else :class="{ 'pl-dim': isLoader }">
        <div v-for="row in rows" :key="row.id" class="pl-group">
          <div
            class="pl-row pl-product-row"
            :class="{ clickable: auth.can('STOCK') }"
            :role="auth.can('STOCK') ? 'link' : undefined"
            :tabindex="auth.can('STOCK') ? 0 : undefined"
            @click="openProduct(row.id)"
            @keydown.enter="openProduct(row.id)"
          >
            <div class="pl-product">
              <img class="pl-thumb" :src="row.pic || require('@/assets/images/NoImage.png')" alt="" />
              <div class="pl-name">
                <b>{{ row.name }}</b>
                <span class="pl-muted">
                  {{ row.magasin?.name || row.customer_name || "—" }}
                  <template v-if="row.stock_agency_name"> · {{ row.stock_agency_name }}</template>
                </span>
              </div>
            </div>
            <span class="pl-muted" data-label="Référence">
              {{ hasVariants(row) ? `${row.inventories.length} variantes` : row.inventories?.[0]?.inventory_ref || "—" }}
            </span>
            <div class="num" data-label="En stock">
              <b v-if="hasVariants(row)" :class="{ zero: totals(row).stock === 0 }">{{ totals(row).stock }}</b>
              <span v-else class="pl-qty" :class="productLevel(row)">{{ totals(row).stock }}</span>
            </div>
            <div class="num" data-label="Pas encore reçu">
              <b :class="totals(row).pending ? 'warn' : 'zero'">{{ totals(row).pending || "–" }}</b>
            </div>
            <div class="pl-status" data-label="Statut">
              <span class="pl-pill" :class="row.received == 1 ? 'ok' : 'wait'">
                {{ row.received == 1 ? "Reçu" : "En attente" }}
              </span>
              <span v-if="row.received_time" class="pl-muted">le {{ getDate(row.received_time) }}</span>
            </div>
            <div class="pl-go">
              <button
                v-if="!hasVariants(row) && row.inventories?.[0] && canToggleArchive(row.inventories[0])"
                type="button"
                class="pl-archive"
                :disabled="archiving === row.inventories[0].id"
                @click.stop="toggleArchive(row.inventories[0])"
              >
                {{ row.inventories[0].archived == 1 ? "Désarchiver" : "Archiver" }}
              </button>
              <template v-if="auth.can('STOCK')">
                <span class="pl-edit">Modifier</span>
                <span aria-hidden="true">›</span>
              </template>
            </div>
          </div>

          <div v-if="hasVariants(row)" class="pl-variants">
            <div
              v-for="v in row.inventories"
              :key="v.id"
              class="pl-row pl-variant-row"
              :class="{ archived: v.archived == 1 }"
            >
              <span class="pl-vname">
                <span class="pl-branch" aria-hidden="true">└</span>
                <span class="pl-vtext">{{ v.var_name || row.name }}</span>
                <span v-if="v.archived == 1" class="pl-pill arch">Archivé</span>
              </span>
              <span class="pl-muted" data-label="Référence">{{ v.inventory_ref || "—" }}</span>
              <div class="num" data-label="En stock">
                <span class="pl-qty" :class="stockLevel(v)">{{ v.qty ?? 0 }}</span>
              </div>
              <div class="num" data-label="Pas encore reçu">
                <b v-if="Number(v.qty_not_received) > 0" class="warn">+{{ v.qty_not_received }}</b>
                <span v-else class="zero">–</span>
              </div>
              <span></span>
              <div class="pl-go">
                <button
                  v-if="canToggleArchive(v)"
                  type="button"
                  class="pl-archive"
                  :disabled="archiving === v.id"
                  @click="toggleArchive(v)"
                >
                  {{ v.archived == 1 ? "Désarchiver" : "Archiver" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div v-if="rows.length" class="pl-legend">
        <span><i class="dot ok"></i>En stock</span>
        <span><i class="dot low"></i>Stock faible (≤ {{ LOW_STOCK }})</span>
        <span><i class="dot out"></i>Rupture</span>
        <span><i class="dot arch"></i>Archivé (hors total)</span>
        <span v-if="auth.can('STOCK')">Seule une variante à 0 peut être archivée.</span>
      </div>

      <!-- Pagination -->
      <div v-if="filter.total > 0" class="pl-footer">
        <span class="pl-muted">
          {{ (filter.current_page - 1) * filter.per_page + 1 }}–{{ Math.min(filter.current_page * filter.per_page, filter.total) }}
          sur {{ filter.total }}
        </span>
        <div class="pl-pager">
          <button type="button" :disabled="filter.current_page === 1" @click="goToPage(filter.current_page - 1)">‹ Précédent</button>
          <span>Page {{ filter.current_page }} / {{ filter.last_page }}</span>
          <button type="button" :disabled="filter.current_page === filter.last_page" @click="goToPage(filter.current_page + 1)">Suivant ›</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";
import { useAuthStore } from "../../stores/auth";
import { useAppToast } from "@/composables/useAppToast";
import api from "@/utils/axios";
import MyDateRangePicker from "@/components/MyDateRangePicker.vue";

interface Inventory {
  id: number;
  inventory_ref: string;
  var_name: string | null;
  qty: number | null;
  qty_not_received: number | null;
  archived: number;
}
interface ProductRow {
  id: number;
  name: string;
  pic: string | null;
  received: number;
  received_time: string | null;
  variant: number;
  customer_name?: string;
  stock_agency_name?: string;
  magasin?: { id: number; name: string } | null;
  inventories: Inventory[];
}
interface Option {
  id?: number | string;
  code?: string;
  name: string;
}

// A variant at or under this quantity is shown as "stock faible"
const LOW_STOCK = 5;

export default {
  name: "ProductListPage",
  components: { MyDateRangePicker },
  setup: () => {
    const { showToast } = useAppToast();
    const auth = useAuthStore();
    const router = useRouter();

    const rows = ref<ProductRow[]>([]);
    const isLoader = ref(false);
    const archiving = ref<number | null>(null);

    const filter = reactive({
      searchTerm: "",
      searchMagazin: "",
      searchAgency: "",
      searchStatus: "",
      searchDate: "",
      searchEDate: "",
      per_page: 10,
      total: 0,
      last_page: 1,
      current_page: 1,
    });

    const searchTerm = ref("");
    const client_filter = ref<Option | null>(null);
    const agency_filter = ref<Option | null>(null);
    const status_filter = ref("");
    const selectedDate = ref<any>(null);
    const showCalendarBox = ref(false);

    const clientList = ref<Option[]>([]);
    const agenciesList = ref<Option[]>([]);

    const hasFilters = computed(
      () =>
        !!(filter.searchTerm || filter.searchMagazin || filter.searchAgency || filter.searchStatus || filter.searchDate)
    );

    function getDate(value: string | null) {
      return value ? moment(String(value)).format("DD/MM/YYYY HH:mm") : "";
    }

    async function getRecords() {
      isLoader.value = true;
      try {
        const params = new URLSearchParams({
          searchTerm: filter.searchTerm,
          searchMagazin: String(filter.searchMagazin || ""),
          searchAgency: String(filter.searchAgency || ""),
          searchStatus: filter.searchStatus,
          searchDate: filter.searchDate,
          searchEDate: filter.searchEDate,
          page: String(filter.current_page),
          per_page: String(filter.per_page),
        });
        const { data } = await api.get(`/api/auth/list_product?${params}`);
        rows.value = data.users.data;
        filter.current_page = data.users.current_page;
        filter.per_page = data.users.per_page;
        filter.total = data.users.total;
        filter.last_page = data.users.last_page || 1;
      } finally {
        isLoader.value = false;
      }
    }

    function applyFilters() {
      filter.searchMagazin = (client_filter.value?.id as string) || "";
      filter.searchAgency = (agency_filter.value?.id as string) || "";
      filter.searchStatus = status_filter.value || "";
      filter.searchDate = selectedDate.value?.[0] ? selectedDate.value[0].format("YYYY-MM-DD") : "";
      filter.searchEDate = selectedDate.value?.[1] ? selectedDate.value[1].format("YYYY-MM-DD") : "";
      filter.current_page = 1;
      getRecords();
    }

    // Filters apply as soon as they change; a date range waits for both ends.
    // `resetting` stops the watchers from reloading again during a reset.
    let resetting = false;
    watch([client_filter, agency_filter, status_filter], () => {
      if (!resetting) applyFilters();
    });
    watch(selectedDate, (d) => {
      if (!resetting && (!d || (d[0] && d[1]))) applyFilters();
    });

    // Search as the user types, without firing a request on every key
    let searchTimer: ReturnType<typeof setTimeout> | undefined;
    function onSearch() {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        filter.searchTerm = searchTerm.value.trim();
        filter.current_page = 1;
        getRecords();
      }, 350);
    }

    function clearSearch() {
      clearTimeout(searchTimer);
      searchTerm.value = "";
      filter.searchTerm = "";
      filter.current_page = 1;
      getRecords();
    }

    function removeFilter() {
      clearTimeout(searchTimer);
      searchTerm.value = "";
      filter.searchTerm = "";
      resetting = true;
      client_filter.value = null;
      agency_filter.value = null;
      status_filter.value = "";
      selectedDate.value = null;
      applyFilters();
      nextTick(() => (resetting = false));
    }

    function showCalendar() {
      showCalendarBox.value = !showCalendarBox.value;
    }

    function goToPage(page: number) {
      if (page < 1 || page > filter.last_page) return;
      filter.current_page = page;
      getRecords();
    }

    // Totals ignore archived variants
    function totals(row: ProductRow) {
      return (row.inventories || [])
        .filter((v) => v.archived != 1)
        .reduce(
          (t, v) => ({ stock: t.stock + Number(v.qty || 0), pending: t.pending + Number(v.qty_not_received || 0) }),
          { stock: 0, pending: 0 }
        );
    }

    // products.variant = 1 when the product was created with variants;
    // otherwise it is shown as one single line
    const hasVariants = (row: ProductRow) => row.variant == 1;

    function stockLevel(v: Inventory) {
      if (v.archived == 1) return "arch";
      const qty = Number(v.qty || 0);
      if (qty <= 0) return "out";
      return qty <= LOW_STOCK ? "low" : "ok";
    }

    const productLevel = (row: ProductRow) =>
      stockLevel({ qty: totals(row).stock, archived: 0 } as Inventory);

    // Only an empty variant can be archived; an archived one can always come back
    const canToggleArchive = (v: Inventory) =>
      auth.can("STOCK") && (v.archived == 1 || Number(v.qty || 0) <= 0);

    async function toggleArchive(v: Inventory) {
      archiving.value = v.id;
      try {
        const { data } = await api.post(`/api/auth/archiveVarProduct/${v.id}`);
        if (data?.message !== "success") throw new Error();
        v.archived = v.archived == 1 ? 0 : 1;
        showToast(v.archived == 1 ? "Variante archivée." : "Variante désarchivée.", "success");
      } catch {
        showToast("Impossible de modifier l'archivage de cette variante.", "error");
      } finally {
        archiving.value = null;
      }
    }

    function openProduct(id: number) {
      if (!auth.can("STOCK")) return;
      router.push({ name: "EditProductPage", params: { id } });
    }

    async function fetchAgencies() {
      try {
        const { data } = await api.get("/api/auth/AgencyStock");
        agenciesList.value = data || [];
      } catch (error) {
        console.error("Error fetching agencies:", error);
      }
    }

    let clientTimer: ReturnType<typeof setTimeout> | undefined;
    function filterClients(search: string) {
      const query = search.trim().toLowerCase();
      clearTimeout(clientTimer);
      if (!query) return;
      clientTimer = setTimeout(async () => {
        try {
          const res = await api.get(`/api/auth/ListCustsFilter`, { params: { query } });
          clientList.value = res.data;
        } catch (error) {
          console.error("Error fetching clients:", error);
          clientList.value = [];
        }
      }, 300);
    }

    onMounted(() => {
      getRecords();
      fetchAgencies();
    });

    return {
      auth,
      rows,
      isLoader,
      archiving,
      filter,
      searchTerm,
      client_filter,
      agency_filter,
      status_filter,
      selectedDate,
      showCalendarBox,
      clientList,
      agenciesList,
      hasFilters,
      LOW_STOCK,
      getDate,
      onSearch,
      clearSearch,
      removeFilter,
      showCalendar,
      goToPage,
      totals,
      hasVariants,
      stockLevel,
      productLevel,
      canToggleArchive,
      toggleArchive,
      openProduct,
      filterClients,
    };
  },
};
</script>

<style scoped>
.pl {
  --pl-accent: #1d4ed8;
  --pl-accent-hover: #1e40af;
  --pl-text: #1d2330;
  --pl-muted: #5f6878;
  --pl-line: #eceef2;
  --pl-border: #d5d9e0;
  --pl-warn: #9a4a06;
  color: var(--pl-text);
  margin-bottom: 25px;
}

.pl-topbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.pl-title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
}
.pl-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--pl-muted);
}

.pl-btn {
  height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  border: 1px solid transparent;
  transition: background 0.15s;
}
.pl-btn-primary {
  background: var(--pl-accent);
  color: #fff;
}
.pl-btn-primary:hover {
  background: var(--pl-accent-hover);
  color: #fff;
}
.pl-btn-ghost {
  background: #fff;
  border-color: var(--pl-border);
  color: var(--pl-text);
}

.pl-card {
  background: #fff;
  border: 1px solid var(--pl-line);
  border-radius: 14px;
  overflow: visible;
}

/* Toolbar / filters */
.pl-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--pl-line);
}
.pl-search {
  position: relative;
  flex: 1 1 240px;
  max-width: 320px;
}
.pl-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--pl-muted);
  font-size: 14px;
  line-height: 1;
}
.pl-search input {
  width: 100%;
  height: 42px;
  padding: 0 36px 0 38px;
  border: 1px solid var(--pl-border);
  border-radius: 10px;
  font-size: 14px;
  outline: none;
}
.pl-search input:focus {
  border-color: var(--pl-accent);
  box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.12);
}
.pl-search input::-webkit-search-cancel-button {
  display: none;
}
.pl-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  font-size: 20px;
  color: var(--pl-muted);
  line-height: 1;
}
.pl-filter {
  flex: 0 1 170px;
  min-width: 150px;
}
.pl-filter-wide {
  flex-basis: 210px;
}
.pl-filter .form-select {
  height: 42px;
  border-radius: 10px;
  border-color: var(--pl-border);
  font-size: 14px;
}
.pl-date {
  position: relative;
  flex-basis: 250px;
}
.pl-date-btn {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--pl-border);
  border-radius: 10px;
  background: #fff;
  font-size: 13.5px;
  color: var(--pl-text);
  white-space: nowrap;
  overflow: hidden;
}
.pl-date-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 999;
  padding: 8px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(20, 25, 40, 0.15);
}
.pl-reset {
  height: 42px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--pl-accent);
}
.pl-reset:hover {
  background: #eef2ff;
}

/* Table grid: product/variant | ref | stock | pending | status | action */
.pl-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px 100px 130px 170px 150px;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
}
.pl-head {
  height: 44px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--pl-muted);
  background: #f8f9fb;
  border-bottom: 1px solid var(--pl-line);
}
.num {
  text-align: right;
}
.pl-group + .pl-group {
  border-top: 1px solid var(--pl-line);
}

/* Product line */
.pl-product-row {
  min-height: 68px;
  padding-top: 8px;
  padding-bottom: 8px;
  transition: background 0.12s;
}
.pl-product-row.clickable {
  cursor: pointer;
}
.pl-product-row.clickable:hover,
.pl-product-row.clickable:focus-visible {
  background: #f5f8ff;
  outline: none;
}
.pl-product-row:hover .pl-edit {
  opacity: 1;
}
.pl-product {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.pl-thumb {
  width: 42px;
  height: 42px;
  flex: none;
  object-fit: cover;
  border-radius: 9px;
  border: 1px solid var(--pl-line);
  background: #f3f4f6;
}
.pl-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pl-name b {
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pl-product-row .num b {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}
.pl-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}

/* Variant lines */
.pl-variants {
  background: #fafbfc;
  padding-bottom: 6px;
}
.pl-variant-row {
  min-height: 42px;
  font-size: 13.5px;
}
.pl-vname {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 30px;
  color: #3b4352;
  min-width: 0;
}
.pl-branch {
  color: #b3b9c4;
}
.pl-variant-row.archived .pl-vtext {
  color: #9aa1ad;
  text-decoration: line-through;
}

.pl-muted {
  font-size: 12.5px;
  color: var(--pl-muted);
}
.zero {
  color: #9aa1ad;
}
.warn {
  color: var(--pl-warn);
}

/* Quantity badge */
.pl-qty {
  display: inline-block;
  min-width: 36px;
  padding: 5px 8px;
  border-radius: 7px;
  text-align: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
.pl-qty.ok {
  background: #1e8a45;
}
.pl-qty.low {
  background: #c26a05;
}
.pl-qty.out {
  background: #c4281c;
}
.pl-qty.arch {
  background: #c3c8d0;
}

.pl-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.pl-pill.ok {
  background: #e7f6ec;
  color: #1e6b37;
}
.pl-pill.wait {
  background: #fff1dc;
  color: var(--pl-warn);
}
.pl-pill.arch {
  padding: 2px 8px;
  font-size: 11px;
  background: #eceef2;
  color: var(--pl-muted);
}

.pl-go {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  color: var(--pl-muted);
  font-size: 20px;
}
.pl-edit {
  font-size: 13px;
  font-weight: 600;
  color: var(--pl-accent);
  opacity: 0;
  transition: opacity 0.12s;
}
.pl-archive {
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--pl-border);
  border-radius: 8px;
  background: #fff;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--pl-text);
}
.pl-archive:hover {
  border-color: var(--pl-muted);
}
.pl-archive:disabled {
  opacity: 0.5;
}

/* Legend */
.pl-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  padding: 12px 20px;
  border-top: 1px solid var(--pl-line);
  font-size: 12.5px;
  color: var(--pl-muted);
}
.pl-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}
.dot.ok {
  background: #1e8a45;
}
.dot.low {
  background: #c26a05;
}
.dot.out {
  background: #c4281c;
}
.dot.arch {
  background: #c3c8d0;
}

/* States */
.pl-dim {
  opacity: 0.55;
  pointer-events: none;
}
.pl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 56px 20px;
  text-align: center;
  color: var(--pl-muted);
}
.pl-empty b {
  color: var(--pl-text);
  font-size: 16px;
}
.pl-skeleton {
  grid-template-columns: 1fr;
  min-height: 68px;
}
.sk {
  background: linear-gradient(90deg, #f0f2f5 25%, #e6e9ee 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: pl-shimmer 1.2s infinite;
}
.sk-line {
  width: 40%;
  height: 14px;
  border-radius: 6px;
}
@keyframes pl-shimmer {
  to {
    background-position: -200% 0;
  }
}

/* Pagination */
.pl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 20px;
  border-top: 1px solid var(--pl-line);
}
.pl-pager {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13.5px;
}
.pl-pager button {
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--pl-border);
  border-radius: 8px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 600;
}
.pl-pager button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Mobile: product line on top, variants as compact lines */
@media (max-width: 767px) {
  .pl-head {
    display: none;
  }
  .pl-search,
  .pl-filter {
    flex: 1 1 100%;
    max-width: none;
  }
  .pl-product-row {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    padding: 14px 16px;
  }
  .pl-product-row .pl-product,
  .pl-product-row .pl-status {
    grid-column: 1 / -1;
  }
  .pl-product-row [data-label] {
    text-align: left;
  }
  .pl-product-row [data-label]::before {
    content: attr(data-label);
    display: block;
    font-size: 11.5px;
    color: var(--pl-muted);
    margin-bottom: 2px;
  }
  .pl-edit,
  .pl-product-row .pl-go > span {
    display: none;
  }
  .pl-product-row .pl-go {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
  .pl-variant-row {
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    padding: 6px 16px;
  }
  .pl-variant-row > :nth-child(2),
  .pl-variant-row > :nth-child(5) {
    display: none;
  }
  .pl-vname {
    padding-left: 8px;
  }
  .pl-toolbar,
  .pl-footer,
  .pl-legend {
    padding: 12px 16px;
  }
}
</style>
